import React from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Section from '../components/Section';
import PriceCard from '../components/PriceCard';
import FAQ from '../components/FAQ';
import { FaCheck, FaTimes, FaRegLightbulb, FaGoogle, FaEnvelope, FaCalendarAlt } from 'react-icons/fa';

const PricingHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const PricingTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 1rem;
  display: inline-block;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  }
`;

const PricingDescription = styled.p`
  font-size: 1.25rem;
  color: var(--gray);
  max-width: 700px;
  margin: 0 auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const AdditionalServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 4rem;
`;

const MaintenanceInfo = styled.div`
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
  border-radius: var(--border-radius);
  padding: 2rem;
  margin: 3rem auto;
  max-width: 900px;
  display: flex;
  align-items: center;
  box-shadow: var(--box-shadow-sm);
`;

const MaintenanceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  flex-shrink: 0;
  
  svg {
    font-size: 1.8rem;
    color: var(--white);
  }
`;

const MaintenanceContent = styled.div`
  flex: 1;
`;

const MaintenanceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--gray-dark);
`;

const MaintenanceDescription = styled.p`
  color: var(--gray);
  margin-bottom: 0;
  font-size: 1.05rem;
`;

const ServiceCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow-lg);
  }
`;

const ServiceIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  svg {
    font-size: 1.5rem;
    color: var(--white);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--gray-dark);
`;

const ServicePrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--blue);
  margin-bottom: 0.75rem;
`;

const ServiceDescription = styled.p`
  color: var(--gray);
  margin-bottom: 1rem;
  flex-grow: 1;
`;

const PreciosPage = () => {
  // Planes de precios estandarizados
  const basicPlan = {
    title: 'Web Básica',
    price: 200,
    period: 'pago único',
    description: 'Ideal para profesionales o pequeños negocios que quieren establecer su presencia online.',
    features: [
      { text: 'Hasta 5 páginas', included: true },
      { text: 'Adaptada a móviles y tablets', included: true },
      { text: 'Carga rápida con tecnología moderna', included: true },
      { text: 'Formulario de contacto funcional', included: true },
      { text: 'Botón de WhatsApp visible', included: true },
      { text: 'Enlaces a tus redes sociales', included: true },
      { text: 'Textos legales incluidos', included: true },
      { text: 'Análisis básico de palabras clave', included: true },
      { text: 'Sección de testimonios de clientes', included: true },
      { text: 'Dominio .es Gratis', included: false },
      { text: 'Ficha de Google My Business', included: false },
      { text: 'Email Corporativo 1GB', included: false },
    ],
    buttonText: 'Elegir Web Básica',
    buttonLink: '/reserva?plan=basico',
  };
  
  const professionalPlan = {
    title: 'Web Profesional',
    price: 300,
    period: 'pago único',
    description: 'Para negocios que necesitan una web más completa con mejor posicionamiento.',
    features: [
      { text: 'Hasta 10 páginas (más contenido)', included: true },
      { text: 'Adaptada a móviles y tablets', included: true },
      { text: 'Carga rápida con tecnología moderna', included: true },
      { text: 'Formulario de contacto funcional', included: true },
      { text: 'Botón de WhatsApp visible', included: true },
      { text: 'Enlaces a tus redes sociales', included: true },
      { text: 'Textos legales incluidos', included: true },
      { text: 'Análisis básico de palabras clave', included: true },
      { text: 'Sección de testimonios de clientes', included: true },
      { text: 'Dominio .es Gratis', included: false },
      { text: 'Ficha de Google My Business', included: false },
      { text: 'Email Corporativo 1GB', included: false },
    ],
    isPopular: true,
    buttonText: 'Elegir Web Profesional',
    buttonLink: '/reserva?plan=profesional',
  };
  
  const advancedPlan = {
    title: 'Web Avanzada',
    price: 400,
    period: 'pago único',
    description: 'Solución completa para empresas que necesitan destacar con funcionalidades avanzadas.',
    features: [
      { text: 'Hasta 15 páginas (contenido extenso y detallado)', included: true },
      { text: 'Adaptada a móviles y tablets', included: true },
      { text: 'Carga rápida con tecnología moderna', included: true },
      { text: 'Formulario de contacto funcional', included: true },
      { text: 'Botón de WhatsApp visible', included: true },
      { text: 'Enlaces a tus redes sociales', included: true },
      { text: 'Textos legales incluidos', included: true },
      { text: 'Análisis básico de palabras clave', included: true },
      { text: 'Sección de testimonios de clientes', included: true },
      { text: 'Dominio .es Gratis', included: true },
      { text: 'Ficha de Google My Business', included: true },
      { text: 'Email Corporativo 1GB', included: true },
    ],
    buttonText: 'Elegir Web Avanzada',
    buttonLink: '/reserva?plan=avanzado',
  };
  
  // Plan Landing Page (conservado de la versión anterior)
  const landingPlan = {
    title: 'Landing Page',
    price: 150,
    period: 'pago único',
    description: 'Perfecta para campañas específicas o promociones puntuales.',
    features: [
      { text: '1 página optimizada', included: true },
      { text: 'Diseño responsive', included: true },
      { text: 'Optimización SEO básica', included: true },
      { text: 'Formulario de contacto', included: true },
      { text: 'Enlaces a redes sociales', included: true },
      { text: 'Integración de WhatsApp', included: true },
      { text: 'Aviso legal y políticas', included: true },
      { text: 'Enfoque en conversión', included: true },
      { text: 'Entrega en 3 días', included: true },
      { text: 'Conexión con Google Analytics', included: false },
    ],
    buttonText: 'Elegir landing page',
    buttonLink: '/reserva?plan=landing',
  };
  
  // Datos para preguntas frecuentes con sistema de acordeón
  const faqItems = [
    {
      id: '1',
      question: '¿Qué incluye el mantenimiento mensual?',
      answer: 'El mantenimiento mensual de 20€ (sin IVA) incluye hosting en nuestros servidores, dominio, certificado SSL, copias de seguridad, actualizaciones de seguridad y soporte técnico. También incluye pequeñas modificaciones como cambios de textos o imágenes que nosotros realizaremos por ti. Se factura mensualmente y es necesario para mantener tu web activa.',
      category: 'Mantenimiento'
    },
    {
      id: '2',
      question: '¿Hay algún coste oculto o adicional?',
      answer: 'No, los precios mostrados son transparentes e incluyen todo lo especificado en cada plan. Además del precio de desarrollo inicial, existe una cuota de mantenimiento mensual de 20€ (sin IVA) que es obligatoria para mantener tu web activa. Los servicios como correo electrónico corporativo o Google My Business se pueden contratar aparte según tus necesidades.',
      category: 'Precios'
    },
    {
      id: '3',
      question: '¿Cuánto tiempo tarda en estar lista mi web?',
      answer: 'El tiempo de entrega depende de la complejidad del proyecto, pero generalmente completamos los proyectos en aproximadamente una semana. Las landing pages se entregan en un plazo máximo de 3 días. Es importante que nos proporciones toda la información y materiales necesarios lo antes posible para agilizar el proceso.',
      category: 'Proceso'
    },
    {
      id: '4',
      question: '¿Cómo solicito cambios en mi web después de la entrega?',
      answer: 'Si necesitas modificaciones en tu web, simplemente contáctanos por email o teléfono. Para cambios pequeños (textos, imágenes, datos de contacto), los realizaremos sin coste adicional como parte del servicio de mantenimiento. Para cambios más sustanciales, te proporcionaremos un presupuesto específico antes de realizar cualquier trabajo.',
      category: 'Mantenimiento'
    },
    {
      id: '5',
      question: '¿Es obligatorio el mantenimiento mensual?',
      answer: 'Sí, el mantenimiento mensual es obligatorio ya que tu web se aloja en nuestros servidores. Este servicio garantiza que tu web esté siempre actualizada, segura y funcionando correctamente. Incluye el hosting, dominio y certificado SSL, que son servicios necesarios para que tu web esté online, además del soporte técnico y las pequeñas modificaciones que puedas necesitar.',
      category: 'Mantenimiento'
    },
    {
      id: '6',
      question: '¿Qué ocurre si quiero añadir nuevas secciones o funcionalidades a mi web más adelante?',
      answer: 'Podemos implementar nuevas secciones o funcionalidades en cualquier momento. El coste dependerá de la complejidad de lo solicitado. Te proporcionaremos un presupuesto detallado y, una vez aprobado, realizaremos los cambios en tu web.',
      category: 'Mantenimiento'
    },
    {
      id: '7',
      question: '¿Puedo tener una tienda online con estos planes?',
      answer: 'Los planes básicos no incluyen funcionalidades de comercio electrónico. Si necesitas una tienda online, podemos desarrollarla como un proyecto personalizado con un presupuesto específico. Contáctanos para recibir una cotización adaptada a tus necesidades de ecommerce.',
      category: 'Servicios'
    },
    {
      id: '8',
      question: '¿Cómo se realiza el pago?',
      answer: 'Para iniciar el proyecto, solicitamos un pago inicial del 50% del precio total. El 50% restante se abona una vez finalizado el desarrollo y antes de la publicación final. Aceptamos transferencias bancarias y pagos con tarjeta. El mantenimiento mensual se cobra mediante domiciliación bancaria o transferencia recurrente a partir de la publicación de la web.',
      category: 'Precios'
    },
    {
      id: '9',
      question: '¿Qué pasa si no estoy satisfecho con el diseño?',
      answer: 'Trabajamos con un proceso iterativo que incluye tu aprobación en cada fase. Antes de comenzar la programación, te presentamos un diseño para su aprobación. Si no estás satisfecho, realizamos los ajustes necesarios hasta que estés conforme. Una vez aprobado el diseño y comenzada la programación, los cambios estructurales pueden tener un coste adicional.',
      category: 'Proceso'
    },
    {
      id: '10',
      question: '¿Ofrecéis alguna garantía?',
      answer: 'Sí, ofrecemos una garantía de funcionamiento de 30 días después de la entrega. Durante este período, corregiremos cualquier error o problema técnico sin coste adicional. Esta garantía no cubre cambios en el diseño o funcionalidades que no estuvieran en el acuerdo inicial.',
      category: 'Servicios'
    },
    {
      id: '11',
      question: '¿Qué tipos de correo electrónico ofrecéis?',
      answer: 'Ofrecemos tres planes de correo electrónico: Básico (1GB, ideal para recepción de mensajes con 3 cuentas incluidas a 2€/mes), Profesional (10GB por cuenta a 5€/mes, perfecto para uso diario con adjuntos) y Avanzado (50GB por cuenta a 7€/mes para usuarios intensivos). Todos son correos IMAP alojados en nuestro servidor.',
      category: 'Servicios Adicionales'
    },
    {
      id: '12',
      question: '¿En qué consiste el servicio de Sistema de Reservas online?',
      answer: 'El servicio de integración de Reservas Online (100€) incluye la implementación de un sistema de reservas en tu web. Este precio cubre la integración del sistema, no la suscripción a la plataforma de reservas en sí. Es ideal para restaurantes, clínicas, peluquerías o cualquier negocio que necesite un sistema de citas.',
      category: 'Servicios Adicionales'
    },
    {
      id: '13',
      question: '¿Con qué frecuencia puedo solicitar cambios en mi web?',
      answer: 'Con el servicio de mantenimiento mensual, puedes solicitar pequeños cambios (textos, imágenes, datos de contacto) cuando lo necesites, sin límite de frecuencia. Sin embargo, para un funcionamiento más eficiente, recomendamos agrupar varias peticiones pequeñas en lugar de hacer solicitudes individuales.',
      category: 'Mantenimiento'
    },
    {
      id: '14',
      question: '¿Puedo migrar mi web a otro servidor más adelante?',
      answer: 'Nuestro modelo de negocio se basa en alojar y mantener tu web en nuestros servidores. No contemplamos la migración a servidores externos ni proporcionamos acceso al código para trasladar la web. Si deseas cambiar de proveedor en el futuro, deberías contratar un nuevo desarrollo con ese proveedor.',
      category: 'Mantenimiento'
    },
    {
      id: '15',
      question: '¿Qué sucede si decido cancelar el mantenimiento mensual?',
      answer: 'Si decides cancelar el mantenimiento mensual, tu web dejará de estar activa, ya que el alojamiento en nuestros servidores está incluido en ese servicio. Te proporcionaríamos una copia de tus contenidos para que puedas utilizarlos en otro proveedor si lo deseas, pero no el código fuente del sitio.',
      category: 'Mantenimiento'
    }
  ];
  
  return (
    <Layout>
      <SEO 
        title="Precios | Páginas Web Baratas y Económicas" 
        description="Conoce nuestros precios para el diseño y desarrollo de páginas web económicas. Planes adaptados a tus necesidades desde 150€, sin costes ocultos." 
        keywords="precios páginas web baratas, costo sitio web económico, cuánto cuesta una web barata"
        canonical="https://webabajocoste.es/precios"
      />
      
      <Section background="light">
        <PricingHeader>
          <PricingTitle>Precios transparentes sin sorpresas</PricingTitle>
          <PricingDescription>
            Ofrecemos soluciones adaptadas a cada necesidad con precios claros y sin costes ocultos. 
            Todas nuestras webs se pagan una única vez, y solo pagas el mantenimiento mensual si lo deseas.
          </PricingDescription>
        </PricingHeader>
        
        <PricingGrid>
          <PriceCard {...basicPlan} />
          <PriceCard {...professionalPlan} />
          <PriceCard {...advancedPlan} />
        </PricingGrid>
        
        <MaintenanceInfo>
          <MaintenanceIcon>
            <FaRegLightbulb />
          </MaintenanceIcon>
          <MaintenanceContent>
            <MaintenanceTitle>Mantenimiento mensual: 20€/mes</MaintenanceTitle>
            <MaintenanceDescription>
              Incluye hosting en nuestros servidores, dominio, certificado SSL, copias de seguridad, actualizaciones de seguridad
              y soporte técnico. Este servicio es obligatorio ya que tu web se aloja exclusivamente en nuestros servidores.
            </MaintenanceDescription>
          </MaintenanceContent>
        </MaintenanceInfo>
        
        <PricingHeader>
          <PricingTitle>Servicios adicionales</PricingTitle>
          <PricingDescription>
            Complementa tu web con estos servicios para mejorar tu presencia online y ofrecer más a tus clientes.
          </PricingDescription>
        </PricingHeader>
        
        <AdditionalServiceGrid>
          <ServiceCard>
            <ServiceIcon>
              <FaGoogle />
            </ServiceIcon>
            <ServiceTitle>Ficha de Google My Business</ServiceTitle>
            <ServicePrice>50€ <small>pago único</small></ServicePrice>
            <ServiceDescription>
              Creación y optimización de tu ficha en Google My Business para aparecer en los resultados locales y Google Maps.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>
              <FaEnvelope />
            </ServiceIcon>
            <ServiceTitle>Correo Básico 1GB</ServiceTitle>
            <ServicePrice>2€/mes <small>3 cuentas incluidas</small></ServicePrice>
            <ServiceDescription>
              Ideal para recepción de mensajes. Acceso IMAP con 1GB de almacenamiento por cuenta. Incluye 3 cuentas.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>
              <FaEnvelope />
            </ServiceIcon>
            <ServiceTitle>Correo Profesional 10GB</ServiceTitle>
            <ServicePrice>5€/mes <small>por cuenta</small></ServicePrice>
            <ServiceDescription>
              Perfecto para uso diario con adjuntos. Acceso IMAP con 10GB de almacenamiento por cuenta.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>
              <FaEnvelope />
            </ServiceIcon>
            <ServiceTitle>Correo Avanzado 50GB</ServiceTitle>
            <ServicePrice>7€/mes <small>por cuenta</small></ServicePrice>
            <ServiceDescription>
              Para usuarios intensivos que necesitan máxima capacidad. Acceso IMAP con 50GB de almacenamiento por cuenta.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceIcon>
              <FaCalendarAlt />
            </ServiceIcon>
            <ServiceTitle>Sistema de Reservas Online</ServiceTitle>
            <ServicePrice>100€ <small>integración</small></ServicePrice>
            <ServiceDescription>
              Integración de un sistema de reservas en tu web. Ideal para restaurantes, clínicas, peluquerías y servicios por cita.
            </ServiceDescription>
          </ServiceCard>
        </AdditionalServiceGrid>
        
        <PricingHeader>
          <PricingTitle>Landing Pages</PricingTitle>
          <PricingDescription>
            ¿Necesitas una página específica para una campaña o promoción? 
            Nuestra landing page está diseñada para maximizar conversiones.
          </PricingDescription>
        </PricingHeader>
        
        <PricingGrid>
          <PriceCard {...landingPlan} />
        </PricingGrid>
      </Section>
      
      <Section>
        <FAQ
          title="Preguntas frecuentes"
          subtitle="Respuestas a las dudas más comunes sobre nuestros precios y servicios"
          items={faqItems}
          showCategories={true}
        />
      </Section>
    </Layout>
  );
};

export default PreciosPage; 